<template>
  <WdsStack gap-md align-center class="tw-px-2">
    <WdsStack direction-column align-center>
      <div
        class="tw-p-2 tw-rounded-[40px]"
        :class="{
          'tw-bg-orange-100': requiresApproval,
          'tw-bg-red-100': rejectedApproval,
        }"
      >
        <WdsIcon
          :name="WdsIconNameEnum.STAMP"
          :size="18"
          :color="requiresApproval ? '#FD8A33' : '#EB2E2E'"
        />
      </div>
    </WdsStack>
    <WdsStack direction-column class="tw-grow">
      <WdsText :text="title" />
      <div class="tw-text-sm tw-font-medium tw-text-gray-700">
        {{ description }}
      </div>
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import { WdsIcon, WdsIconNameEnum, WdsStack, WdsText } from '@worksome/wds-components'
import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useHireApprovalStatuses } from '@/components/Hire/HirePageCompliance/useHireApprovalStatuses'
import { ApprovalTrigger } from '@/gql/graphql';

const props = defineProps<{
  hireId: string
}>()

const { requiresApproval, rejectedApproval, approvalTrigger } = useHireApprovalStatuses(props.hireId)

const title = computed(
  () => {
    if (requiresApproval.value) {
      switch (approvalTrigger.value) {
        case ApprovalTrigger.HireCreated:
          return $t('Hire offer - pending approval') // Created
        case ApprovalTrigger.HireChanged:
          return $t('Contract edited - pending approval') // Updated
        case ApprovalTrigger.ClassificationCreated:
          return $t('Classification created - pending approval') // Classification
      }
    }

    if (rejectedApproval.value) {
      switch (approvalTrigger.value) {
        case ApprovalTrigger.HireCreated:
          return $t('Hire offer - approval denied') // Created
        case ApprovalTrigger.HireChanged:
          return $t('Contract edited - approval denied') // Updated
        case ApprovalTrigger.ClassificationCreated:
          return $t('Classification created - approval denied') // Classification
      }
    }
    return ''
  }
)

const description = computed(
  () => {
    if (requiresApproval.value) {
      switch (approvalTrigger.value) {
        case ApprovalTrigger.HireCreated:
          return $t('Once approved, the offer will be automatically shared with the worker.') // Created
        case ApprovalTrigger.HireChanged:
          return $t('Once approved, the new contract will be automatically shared with the worker for re-signature.') // Updated
        case ApprovalTrigger.ClassificationCreated:
          return $t('Once approved, the classification result will be automatically accepted.') // Classification
      }
    }

    if (rejectedApproval.value) {
      switch (approvalTrigger.value) {
        case ApprovalTrigger.HireCreated:
          return $t('Approval request for the hire was denied internally and hire offer is cancelled.') // Created
        case ApprovalTrigger.HireChanged:
          return $t('Approval request for contract changes were denied internally. The contract remains unchanged.') // Updated
        case ApprovalTrigger.ClassificationCreated:
          return $t('Approval request for the classification created was denied internally. Please redo classification.') // Classification
      }
    }
    return ''
  }
)

</script>
